export enum ProductInfoProductAssetType {
  COLOR_TILE = 'color-tile',
  ENERGY_EFFICIENCY_LABEL = 'energy-efficiency-eel',
  ENERGY_EFFICIENCY_OVERVIEW = 'energy-efficiency-el',
  ENERGY_EFFICIENCY_DATASHEET = 'energy-efficiency-enl',
  PRODUCT_DETAIL_DATASHEET = 'product-datasheet-pdb',
  PRODUCT_INSTRUCTION = 'product-instruction-anl',
  PRODUCT_PACKAGE_INSERT = 'product-packageInsert-bpz',
  PRODUCT_READING_SAMPLE = 'product-readingSample-pdl',
  PRODUCT_SAFETY_DATA_SHEET = 'product-safetyDataSheetAsset-sdb',
  PRODUCT_VIDEO = 'product-video-vd',
  TEST_RATING = 'test-rating-tu',
  TEST_RATING_FRONT_VIEW = 'test-rating-tuv',
  MOBILE_PICTURE = 'mobile-mpv',
  PRODUCT_DECLARATION_OF_CONFORMITY = 'product-declarationOfConformity-dcf',
  PRODUCT_LABORATORY_ANALYSIS = 'product-laboratoryAnalysis-lab'
}

export enum ProductTileProductAssetType {
  ENERGY_EFFICIENCY_LABEL = 'EEL',
  ENERGY_EFFICIENCY_OVERVIEW = 'EL',
  ENERGY_EFFICIENCY_DATASHEET = 'ENL'
}

interface ProductDimension {
  readonly height: number;
  readonly width: number;
}

export interface ProductAsset {
  readonly contentType: string;
  readonly description?: string | null;
  readonly id?: string;
  readonly name: string | null;
  readonly source: string;
}

interface ProductAssetWithDimensions<T extends ProductDimension | null> extends ProductAsset {
  readonly dimensions: T;
}

export type ProductAssetWithOptionalDimensions =
  ProductAssetWithDimensions<ProductDimension | null>;

export type ProductAssetWithRequiredDimensions = ProductAssetWithDimensions<ProductDimension>;

export type ProductAssetWithType<
  T extends ProductAsset,
  U extends ProductInfoProductAssetType | ProductTileProductAssetType
> = T & { readonly type: U };

export type ColorTile = ProductAssetWithType<
  ProductAssetWithRequiredDimensions,
  ProductInfoProductAssetType.COLOR_TILE
>;

export type EnergyEfficiencyLabel = ProductAssetWithType<
  ProductAssetWithRequiredDimensions,
  ProductInfoProductAssetType.ENERGY_EFFICIENCY_LABEL
>;

export type EnergyEfficiencyOverview = ProductAssetWithType<
  ProductAssetWithOptionalDimensions,
  ProductInfoProductAssetType.ENERGY_EFFICIENCY_OVERVIEW
>;

export type EnergyEfficiencyDatasheet = ProductAssetWithType<
  ProductAsset,
  ProductInfoProductAssetType.ENERGY_EFFICIENCY_DATASHEET
>;

export type Datasheet = ProductAssetWithType<
  ProductAsset,
  ProductInfoProductAssetType.PRODUCT_DETAIL_DATASHEET
>;

export type DeclarationOfConformity = ProductAssetWithType<
  ProductAsset,
  ProductInfoProductAssetType.PRODUCT_DECLARATION_OF_CONFORMITY
>;

export type LaboratoryAnalysis = ProductAssetWithType<
  ProductAsset,
  ProductInfoProductAssetType.PRODUCT_LABORATORY_ANALYSIS
>;

export type Instruction = ProductAssetWithType<
  ProductAsset,
  ProductInfoProductAssetType.PRODUCT_INSTRUCTION
>;

export type PackageInsert = ProductAssetWithType<
  ProductAsset,
  ProductInfoProductAssetType.PRODUCT_PACKAGE_INSERT
>;

export type ProductTestRatingAsset = ProductAssetWithType<
  ProductAsset,
  ProductInfoProductAssetType.TEST_RATING
>;

export type ProductTestRatingFrontViewAsset = ProductAssetWithType<
  ProductAssetWithOptionalDimensions,
  ProductInfoProductAssetType.TEST_RATING_FRONT_VIEW
>;

export type ProductMobileImageAsset = ProductAssetWithType<
  ProductAssetWithOptionalDimensions,
  ProductInfoProductAssetType.MOBILE_PICTURE
>;

export type ProductVideoAsset = ProductAssetWithType<
  ProductAsset,
  ProductInfoProductAssetType.PRODUCT_VIDEO
>;

export type ReadingSample = ProductAssetWithType<
  ProductAsset,
  ProductInfoProductAssetType.PRODUCT_READING_SAMPLE
>;

export type SafetyDataSheet = ProductAssetWithType<
  ProductAsset,
  ProductInfoProductAssetType.PRODUCT_SAFETY_DATA_SHEET
>;

export type ProductDatasheet =
  | Datasheet
  | DeclarationOfConformity
  | EnergyEfficiencyDatasheet
  | Instruction
  | LaboratoryAnalysis
  | PackageInsert
  | ReadingSample
  | SafetyDataSheet;

export interface ProductAssetsFields {
  readonly colorTile: ColorTile | null;
  readonly energyEfficiencyLabel: EnergyEfficiencyLabel | null;
  readonly energyEfficiencyOverview: EnergyEfficiencyOverview | null;
  readonly mobileImages: ReadonlyArray<ProductMobileImageAsset>;
  readonly productDataSheets: ReadonlyArray<ProductDatasheet> | null;
  readonly productVideos: ReadonlyArray<ProductVideoAsset>;
  readonly testRatingAsset: ProductTestRatingAsset | null;
  readonly testRatingFrontViewAsset: ProductTestRatingFrontViewAsset | null;
}
